import { useEffect, useMemo, useState } from 'react';
import type { Aspect, Design, SavedTemplate, StylePreset, Template } from '@noorview/core';
import { ASPECTS, LAYOUT_COLLECTIONS, STYLE_PRESETS, TEMPLATES, applyStyle, stylesFor, applyStyleWithLayout, templateWidgets } from '@noorview/core';
import { Select } from '../ui/pickers';
import { Icon } from '../ui/icons';
import { Segmented, Sheet, askText, choose, confirm } from '../ui/controls';
import { StageView } from '../ui/StageView';
import { attempt, backend, toast, useApp } from '../data/store';
import { useStudio } from './store';

/**
 * Three ways to start: Layouts (where widgets go), Styles (a complete look, with or without its layout) and
 * My templates (designs the masjid saved to reuse).
 */

type Tab = 'layouts' | 'styles' | 'mine';

function matches(t: Template) {
  const d = useStudio.getState().design;
  if (!d || !d.widgets.length) return false;
  const want = templateWidgets(t, d).map((w) => `${w.type}${w.x},${w.y},${w.w},${w.h}`).sort().join('|');
  return d.widgets.map((w) => `${w.type}${w.x},${w.y},${w.w},${w.h}`).sort().join('|') === want;
}

const undoToast = (msg: string) => toast(msg, { action: { label: 'Undo', run: () => useStudio.getState().undo() } });

async function applyLayout(t: Template | null, col = 'standard') {
  const st = useStudio.getState();
  const d = st.design!;
  if (d.widgets.length && !(await confirm(t ? `Use the ${t.name} layout?` : 'Start from a blank canvas?', { body: 'This replaces the widgets on this screen’s draft. You can undo it straight away.', ok: t ? 'Use layout' : 'Clear canvas' }))) return false;
  st.change((x) => ({ ...x, widgets: t ? templateWidgets(t, x, col) : [] }));
  st.select(null);
  undoToast(t ? `${t.name} layout applied` : 'Blank canvas');
  return true;
}

async function applyPreset(s: StylePreset) {
  const st = useStudio.getState();
  const d = st.design!;
  let mode: string | null = 'layout';
  if (d.widgets.length) {
    mode = await choose(`Use the ${s.name} style?`, {
      body: `${s.description} You can keep your own layout and just restyle it, or use the style’s layout too.`,
      choices: [{ value: 'restyle', label: 'Style my layout', kind: 'plain' }, { value: 'layout', label: 'Use its layout too', kind: 'primary' }],
    });
  }
  if (!mode) return false;
  st.change((x) => (mode === 'restyle' ? applyStyle(x, s) : applyStyleWithLayout(x, s)));
  st.select(null);
  undoToast(`${s.name} style applied`);
  return true;
}

async function applySaved(t: SavedTemplate) {
  const st = useStudio.getState();
  const d = st.design!;
  const other = t.design.aspect !== d.aspect;
  // A saved template keeps the screen shape it was made for, so nothing is squeezed or rearranged.
  const body = other
    ? `It was made for ${ASPECTS[t.design.aspect].label.toLowerCase()} screens (${t.design.aspect}), so this screen switches to ${t.design.aspect} to show it exactly as saved. You can undo it straight away.`
    : 'This replaces this screen’s draft with your saved template. You can undo it straight away.';
  if ((d.widgets.length || other) && !(await confirm(`Use “${t.name}”?`, { body, ok: 'Use template' }))) return false;
  st.change(() => structuredClone(t.design));
  st.select(null);
  undoToast(other ? `“${t.name}” applied, screen set to ${t.design.aspect}` : `“${t.name}” applied`);
  return true;
}

/** Saves this screen's design as one of the masjid's templates. True once saved. */
export async function saveCurrent(): Promise<boolean> {
  const d = useStudio.getState().design!;
  if (!d.widgets.length) { toast('Add some widgets first, then save the design as a template.', { bad: true }); return false; }
  const name = await askText('Save as a template', { label: 'Template name', value: '', ok: 'Save template' });
  if (!name) return false;
  if (!(await attempt(() => backend.saveTemplate(name, d)))) return false;
  toast(`Saved “${name}” in My templates`);
  return true;
}

export function TemplateThumb({ t, selected, portrait }: { t: Template | null; selected?: boolean; portrait: boolean }) {
  const cells = t ? (portrait ? t.portrait : t.landscape) : [];
  const cols = portrait ? 6 : 12, rows = portrait ? 10 : 8;
  return (
    <span className={`tthumb${selected ? ' sel' : ''}${portrait ? ' portrait' : ''}`} aria-hidden>
      {cells.map(([type, x, y, w, h], i) => (
        <i key={i} className={type === 'prayerTimes' ? 'pt' : ''} style={{ left: `calc(${(x / cols) * 100}% + 3px)`, top: `calc(${(y / rows) * 100}% + 3px)`, width: `calc(${(w / cols) * 100}% - 4px)`, height: `calc(${(h / rows) * 100}% - 4px)` }} />
      ))}
      {!t && <Icon name="plus" size={22} />}
    </span>
  );
}

const RATIO: Record<Aspect, string> = { '16:9': '16 / 9', '9:16': '9 / 16', '21:9': '21 / 9', '4:3': '4 / 3' };

/** A real, still preview of a design, drawn by the screen renderer, at the design's own shape. */
function DesignThumb({ design, portrait, label, selected }: { design: Design; portrait: boolean; label: string; selected?: boolean }) {
  const ws = useApp((s) => s.ws)!;
  // The frame matches this screen; a design made for another shape sits inside it at its own shape.
  const frame = useStudio((s) => s.design!.aspect);
  const odd = frame !== design.aspect;
  return (
    <span className={`tthumb live${portrait ? ' portrait' : ''}${selected ? ' sel' : ''}${odd ? ' odd' : ''}`} style={{ aspectRatio: RATIO[frame] }} aria-hidden>
      <StageView design={design} masjid={ws.masjid} timetable={ws.timetable} quality="balanced" frozen className="stage-fill" label={label} />
    </span>
  );
}

function useStylePreviews(aspect: Design['aspect']) {
  const base = useStudio((s) => s.design!);
  return useMemo(() => stylesFor(aspect).map((s) => ({ s, design: applyStyleWithLayout({ ...base, widgets: [] }, s) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [aspect]);
}

const COL_KEY = 'nv.layoutCollection';
function readCol() { try { const v = localStorage.getItem(COL_KEY); return LAYOUT_COLLECTIONS.some((c) => c.id === v) ? v! : 'standard'; } catch { return 'standard'; } }

/** Live previews of every layout in a collection, on this screen's own background. */
function useLayoutPreviews(col: string) {
  const base = useStudio((s) => s.design!);
  const key = `${base.aspect}|${JSON.stringify(base.background)}|${base.gap}|${col}`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => TEMPLATES.map((t) => ({ t, design: { ...base, widgets: templateWidgets(t, base, col) } })), [key]);
}

export function TemplatesCard() {
  useStudio((s) => s.design?.widgets); // re-render when the layout changes, to update the tick
  const aspect = useStudio((s) => s.design!.aspect);
  const mine = useApp((s) => s.ws?.templates ?? []);
  const portrait = aspect === '9:16';
  // Tall and squarer screens need the height for the canvas, so the strip folds away for 9:16 and 4:3 and opens again
  // for 16:9 and 21:9. It can still be opened or closed by hand; changing the screen shape applies the rule again.
  const roomy = aspect === '16:9' || aspect === '21:9';
  const [open, setOpen] = useState(roomy);
  useEffect(() => { setOpen(roomy); }, [roomy]);
  const [tab, setTab] = useState<Tab>('layouts');
  const [all, setAll] = useState(false);
  const [col, setColState] = useState(readCol);
  const setCol = (v: string) => { setColState(v); try { localStorage.setItem(COL_KEY, v); } catch { /* private window */ } };
  const styles = useStylePreviews(aspect);
  const layouts = useLayoutPreviews(col);
  const n = portrait ? 8 : aspect === '21:9' ? 4 : 5;
  const colInfo = LAYOUT_COLLECTIONS.find((c) => c.id === col)!;
  const colPicker = (
    <Select label="Layout collection" variant="inline" value={col} onChange={setCol} className="tpl-col"
      options={LAYOUT_COLLECTIONS.map((c) => ({ value: c.id, label: c.name }))} />
  );

  const row = tab === 'layouts' ? layouts.slice(0, n).map(({ t, design }) => <LayoutCard key={t.id} t={t} design={design} col={col} portrait={portrait} />)
    : tab === 'styles' ? styles.slice(0, n).map(({ s, design }) => <StyleCard key={s.id} s={s} design={design} portrait={portrait} />)
    : [<SaveCard key="save" portrait={portrait} />, ...mine.slice(-(n - 1)).reverse().map((t) => <MineCard key={t.id} t={t} portrait={portrait} />)];

  return (
    <section className={`card glass templates${open ? ' open' : ''}`} aria-label="Templates">
      <div className="card-head">
        <button type="button" className="tpl-title" onClick={() => setOpen((v) => !v)} aria-hidden tabIndex={-1}><h2>Templates</h2></button>
        <Segmented size="sm" label="Templates" value={tab} onChange={setTab} className="tpl-tabs"
          options={[{ value: 'layouts', label: 'Layouts' }, { value: 'styles', label: 'Styles' }, { value: 'mine', label: `My templates${mine.length ? ` (${mine.length})` : ''}` }]} />
        {tab === 'layouts' && colPicker}
        <span className="spacer" />
        <button type="button" className="link" onClick={() => setAll(true)}>See all</button>
        <button type="button" className="icon-btn sm tpl-toggle" aria-expanded={open} aria-label={open ? 'Hide the templates' : 'Show the templates'} title={open ? 'Hide the templates' : 'Show the templates'} onClick={() => setOpen((v) => !v)}><Icon name="chevronDown" size={18} /></button>
      </div>
      <div className="tpl-body" inert={!open || undefined}>
        <div className={`trow${portrait ? ' portrait' : ''}`} style={{ gridTemplateColumns: `repeat(${tab === 'mine' ? Math.min(n, Math.max(row.length, portrait ? 6 : 4)) : n}, minmax(0, 1fr))` }}>{row}</div>
      </div>
      <Sheet open={all} onClose={() => setAll(false)} size="xl"
        title={tab === 'layouts' ? 'Layouts' : tab === 'styles' ? 'Styles' : 'My templates'}
        subtitle={tab === 'layouts' ? `${colInfo.description} Where widgets go on ${ASPECTS[aspect].label.toLowerCase()} screens; everything stays editable, and you can undo.`
          : tab === 'styles' ? 'Complete looks: background, pattern, colours, fonts and widget designs. Use one with its layout, or restyle your own.'
          : 'Designs your masjid saved. Save the current design to reuse it on any screen.'}>
        <div className={`tgrid${portrait ? ' portrait' : ''}`} onClick={(e) => { if ((e.target as HTMLElement).closest('[data-close-sheet]')) setAll(false); }}>
          {tab === 'layouts' && <>
            <div className="tcols cats" role="group" aria-label="Layout collections">
              {LAYOUT_COLLECTIONS.map((c) => <button key={c.id} type="button" className={`cat${c.id === col ? ' on' : ''}`} aria-pressed={c.id === col} onClick={() => setCol(c.id)}>{c.name}</button>)}
            </div>
            <button type="button" className="tcard" data-close-sheet="" onClick={() => void applyLayout(null)}>
              <TemplateThumb t={null} portrait={portrait} />
              <span className="tlabel">Blank canvas</span><small className="hint">Place every widget yourself.</small>
            </button>
            {layouts.map(({ t, design }) => <LayoutCard key={t.id} t={t} design={design} col={col} portrait={portrait} long />)}
          </>}
          {tab === 'styles' && styles.map(({ s, design }) => <StyleCard key={s.id} s={s} design={design} portrait={portrait} long />)}
          {tab === 'mine' && <>
            <SaveCard portrait={portrait} />
            {[...mine].reverse().map((t) => <MineCard key={t.id} t={t} portrait={portrait} long />)}
          </>}
        </div>
      </Sheet>
    </section>
  );
}

function LayoutCard({ t, design, col, portrait, long }: { t: Template; design: Design; col: string; portrait: boolean; long?: boolean }) {
  const on = matches(t);
  return (
    <button type="button" className="tcard" data-close-sheet="" onClick={() => void applyLayout(t, col)} title={t.description} aria-pressed={on}>
      <DesignThumb design={design} portrait={portrait} label={`${t.name} layout`} selected={on} />
      <span className={`tlabel${on ? ' on' : ''}`}>{on && <Icon name="check" size={14} stroke={3} />}{t.name}</span>
      {long && <small className="hint">{t.description}</small>}
    </button>
  );
}

function StyleCard({ s, design, portrait, long }: { s: StylePreset; design: Design; portrait: boolean; long?: boolean }) {
  return (
    <button type="button" className="tcard" data-close-sheet="" onClick={() => void applyPreset(s)} title={s.description}>
      <DesignThumb design={design} portrait={portrait} label={`${s.name} style`} />
      <span className="tlabel">{s.name}</span>
      {long && <small className="hint">{s.place && <b className="tplace">{s.place}. </b>}{s.description}</small>}
    </button>
  );
}

function SaveCard({ portrait }: { portrait: boolean }) {
  return (
    <button type="button" className="tcard" onClick={() => void saveCurrent()}>
      <span className={`tthumb save${portrait ? ' portrait' : ''}`} aria-hidden><Icon name="plus" size={22} /></span>
      <span className="tlabel">Save current design</span>
    </button>
  );
}

function MineCard({ t, portrait, long }: { t: SavedTemplate; portrait: boolean; long?: boolean }) {
  return (
    <div className="tcard mine">
      <button type="button" className="tcard-hit" data-close-sheet="" onClick={() => void applySaved(t)} aria-label={`Use ${t.name}`}>
        <DesignThumb design={t.design} portrait={portrait} label={t.name} />
      </button>
      <span className="tlabel">{t.name}</span>
      {long && <small className="hint">{t.design.widgets.length} widgets · saved {new Date(t.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</small>}
      <span className="tcard-tools">
        <button type="button" className="lbtn" aria-label={`Rename ${t.name}`} onClick={async () => { const n = await askText('Rename template', { label: 'Template name', value: t.name }); if (n) void attempt(() => backend.renameTemplate(t.id, n)); }}><Icon name="pencil" size={14} /></button>
        <button type="button" className="lbtn danger" aria-label={`Delete ${t.name}`} onClick={async () => { if (await confirm(`Delete “${t.name}”?`, { body: 'Screens using a layout made from it aren’t affected.', ok: 'Delete', danger: true })) void attempt(() => backend.deleteTemplate(t.id), 'Template deleted'); }}><Icon name="trash" size={14} /></button>
      </span>
    </div>
  );
}
